import { useState, useMemo, useEffect, useRef } from "react";
import InputFactory from "../../../../../components/InputFactory";
import FormField from "./FormField";
import regionPh from "../../../../../components/regionsPh.json";
import provincePh from "../../../../../components/provincePh.json";
import municipalityPh from "../../../../../components/municipalityPh.json";
import barangayPh from "../../../../../components/barangayPh.json";
import { objectToOptions, arrayToOptions, formatTinNo } from "./utils";

function AddressForm({
  title,
  description,
  icon = "bi bi-geo-alt",
  formData,
  onChange,
  // regionsOptions,
  // provincesOptions,
  // citiesOptions,
  // barangayOptions,
  type,
}) {
  const [formDatas, setFormDatas] = useState({});
  const [provincesOptions, setProvinces] = useState([]);
  const [citiesOptions, setCities] = useState([]);
  const [barangayOptions, setBarangay] = useState([]);
  const regionsOptions = useMemo(() => objectToOptions(regionPh), []);
  const provinceInput = useRef();
  const cityInput = useRef();
  const barangayInput = useRef();

  useEffect(() => {
    setFormDatas(formData);
  }, [formData]);

  const REGION_FIELDS = ["billingRegion", "shippingRegion"];
  const PROVINCE_FIELDS = ["billingProvince", "shippingProvince"];
  const CITY_FIELDS = ["billingCity", "shippingCity"];

  const handleChange = (value, field) => {
    setFormDatas((prev) => ({ ...prev, [field]: value }));

    // ---------- REGION ----------
    if (REGION_FIELDS.includes(field)) {
      if (!value) {
        provinceInput.current?.onClickClear();
        cityInput.current?.onClickClear();
        barangayInput.current?.onClickClear();
        setProvinces([]);
        setCities([]);
        setBarangay([]);
        onChange(value, field);
        return;
      }

      const region = regionPh.find((r) => r.regionName === value);
      if (!region) {
        setProvinces([]);
        setCities([]);
        setBarangay([]);
        onChange(value, field);
        return;
      }

      const provinces = provincePh.filter((p) => p.regionCode === region.code);

      if (provinces.length) {
        setProvinces(arrayToOptions(provinces));
        setCities([]);
      } else {
        const cities = municipalityPh.filter(
          (m) => m.regionCode === region.code,
        );
        setProvinces([]);
        setCities(arrayToOptions(cities));
      }

      setBarangay([]);
      onChange(value, field);
      return;
    }

    // ---------- PROVINCE ----------
    if (PROVINCE_FIELDS.includes(field)) {
      if (!value) {
        cityInput.current?.onClickClear();
        barangayInput.current?.onClickClear();
        setCities([]);
        setBarangay([]);
        onChange(value, field);
        return;
      }

      const province = provincePh.find((p) => p.name === value);
      if (province) {
        const cities = municipalityPh.filter(
          (c) => c.provinceCode === province.code,
        );
        setCities(arrayToOptions(cities));
      }

      setBarangay([]);
      onChange(value, field);
      return;
    }

    // ---------- CITY ----------
    if (CITY_FIELDS.includes(field)) {
      if (!value) {
        barangayInput.current?.onClickClear();
        setBarangay([]);
        onChange(value, field);
        return;
      }

      const city = municipalityPh.find((c) => c.name === value);
      if (city) {
        const barangays = barangayPh.filter((b) =>
          b.municipalityCode
            ? b.municipalityCode === city.code
            : b.cityCode === city.code,
        );
        setBarangay(arrayToOptions(barangays));
      }

      onChange(value, field);
      return;
    }

    onChange(value, field);
  };

  return (
    <>
      <div className="d-flex flex-column my-2 gap-3 border rounded-3 p-3 border-dark-subtle">
        <div
          className={`col-12 rounded-1 d-flex border-bottom align-items-center pb-1 gap-2`}
        >
          <div className="p-1 rounded-1 bg-light border border-1 px-2">
            <i className={`${icon} text-primary fw-bold`}></i>
          </div>
          <div className="d-flex flex-column gap-0">
            <p className="small fw-bold mb-0 text-primary">{title}</p>
            <p className="small mb-0 text-muted">{description}</p>
          </div>

          <hr />
        </div>

        <FormField label="Region" required>
          <InputFactory
            type="Enum"
            field={type === "billing" ? "billingRegion" : "shippingRegion"}
            required
            placeholder="Select Region"
            indexes={["name"]}
            defaultValue={
              type === "billing"
                ? formDatas?.billingRegion
                : formDatas?.shippingRegion
            }
            options={regionsOptions}
            onChange={handleChange}
          />
        </FormField>

        <FormField label="Province" required={!!provincesOptions.length}>
          <InputFactory
            type="Enum"
            field={type === "billing" ? "billingProvince" : "shippingProvince"}
            placeholder="Select Province"
            required={!!provincesOptions.length}
            disabled={provincesOptions.length === 0}
            defaultValue={
              type === "billing"
                ? formDatas?.billingProvince
                : formDatas?.shippingProvince
            }
            options={provincesOptions}
            className={`border border-1 border-dark-subtle" ${
              !provincesOptions.length ? "bg-dark-subtle" : "bg-white"
            }`}
            onChange={handleChange}
            resetRef={provinceInput}
          />
        </FormField>

        <FormField label="City/Municipality" required>
          <InputFactory
            type="Enum"
            field={type === "billing" ? "billingCity" : "shippingCity"}
            required
            placeholder="Select City/Municipality"
            defaultValue={
              type === "billing"
                ? formDatas?.billingCity
                : formDatas?.shippingCity
            }
            disabled={citiesOptions.length === 0}
            options={citiesOptions}
            className={`border border-1 border-dark-subtle" ${
              !citiesOptions.length ? "bg-dark-subtle" : "bg-white"
            }`}
            onChange={handleChange}
            resetRef={cityInput}
          />
        </FormField>

        <FormField label="Barangay" required>
          <InputFactory
            type="Enum"
            field={type === "billing" ? "billingBarangay" : "shippingBarangay"}
            required
            placeholder="Select Barangay"
            defaultValue={
              type === "billing"
                ? formDatas?.billingBarangay
                : formDatas?.shippingBarangay
            }
            disabled={barangayOptions.length === 0}
            options={barangayOptions}
            className={`border border-1 border-dark-subtle" ${
              !barangayOptions.length ? "bg-dark-subtle" : "bg-white"
            }`}
            onChange={handleChange}
            resetRef={barangayInput}
          />
        </FormField>
      </div>
    </>
  );
}

export default AddressForm;
